import type { AttributionService } from '@/services/attribution/types';
import type { BrowserMetadataService } from '@/services/browser-metadata';
import type { CustomerData } from '@/services/customer/types';
import type { CustomerIdentityService } from '@/services/customer';
import { hasCustomerData } from '@/services/customer';
import type { UrlContextService } from '@/modules/core/url-context';

import type { BasePixelEvent } from '../base';

export class BaseEventBuilder {
  private readonly metadataService: BrowserMetadataService;

  private readonly attribution: AttributionService | null;

  private readonly urlContext: UrlContextService | null;

  private readonly customerIdentity: CustomerIdentityService | null;

  constructor(
    metadataService: BrowserMetadataService,
    attribution?: AttributionService | null,
    urlContext?: UrlContextService | null,
    customerIdentity?: CustomerIdentityService | null
  ) {
    this.metadataService = metadataService;
    this.attribution = attribution ?? null;
    this.urlContext = urlContext ?? null;
    this.customerIdentity = customerIdentity ?? null;
  }

  build<T extends { type: string }>(event: T): T & BasePixelEvent {
    const metadata = this.metadataService.getMetadata();
    const identifiers = this.attribution?.getSnapshot();
    const hasIdentifiers =
      identifiers !== undefined &&
      Object.keys(identifiers).some(key => {
        const value = identifiers[key as keyof typeof identifiers];
        return value !== undefined && value !== null && value !== '';
      });

    const urlSnapshot = this.urlContext?.isEnabled()
      ? this.urlContext.getSnapshot()
      : null;

    const hasQueryParams =
      urlSnapshot !== null && Object.keys(urlSnapshot.queryParams).length > 0;
    const hasUrlContext =
      urlSnapshot !== null &&
      ((urlSnapshot.urlContext.managed &&
        Object.keys(urlSnapshot.urlContext.managed).length > 0) ||
        (urlSnapshot.urlContext.forwarded &&
          Object.keys(urlSnapshot.urlContext.forwarded).length > 0) ||
        (urlSnapshot.urlContext.source &&
          Object.keys(urlSnapshot.urlContext.source).length > 0));

    const customer = this.customerIdentity?.getSnapshot() ?? null;
    const incomingCustomer = (event as { customer?: CustomerData }).customer;
    const resolvedCustomer = incomingCustomer ?? customer;

    return {
      ...event,
      timestamp: Date.now(),
      path: metadata.path,
      url: metadata.url,
      hostname: metadata.hostname,
      language: metadata.language,
      timezone: metadata.timezone,
      ...(hasIdentifiers ? { identifiers } : {}),
      ...(hasQueryParams
        ? { queryParams: { ...urlSnapshot!.queryParams } }
        : {}),
      ...(hasUrlContext
        ? {
            urlContext: {
              ...(urlSnapshot!.urlContext.managed
                ? { managed: { ...urlSnapshot!.urlContext.managed } }
                : {}),
              ...(urlSnapshot!.urlContext.forwarded
                ? { forwarded: { ...urlSnapshot!.urlContext.forwarded } }
                : {}),
              ...(urlSnapshot!.urlContext.source
                ? { source: { ...urlSnapshot!.urlContext.source } }
                : {}),
            },
          }
        : {}),
      ...(hasCustomerData(resolvedCustomer)
        ? { customer: { ...resolvedCustomer } }
        : {}),
    };
  }
}
